// Modal «Pide tu Mini Prixia» de la landing actual (lo abren sus CTAs con useBookDemoStore.open()).
//
// Vault 346 (D8): antes era «Reservar demo» con un selector de día y hora que sólo mandaba correos y no
// quedaba en ningún lado. Ahora no se agenda nada: la solicitud llega a Plataforma › Solicitudes, con
// aviso en la campanita, y desde ahí sale la invitación. Va por el cliente de la app (sin dirección de
// servidor escrita en el código).

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { useBookDemoStore } from '../store/book-demo-store';
import {
  ErrorAlEnviarLaSolicitud,
  cronometroDelFormulario,
  enviarSolicitudDeMiniPrixia,
} from '@/features/solicitudes/services/solicitudes.service';

type Estado = 'idle' | 'submitting' | 'success' | 'error';

export function BookDemoModal() {
  const { isOpen, prefill, close } = useBookDemoStore();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [acepto, setAcepto] = useState(false);
  const [trampa, setTrampa] = useState('');
  const [status, setStatus] = useState<Estado>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const medir = useRef<() => number>(() => 0);

  // Al abrir: el reloj del formulario empieza y se limpia el estado anterior.
  useEffect(() => {
    if (isOpen) {
      medir.current = cronometroDelFormulario();
      setStatus('idle');
      setErrorMsg('');
      if (prefill?.message) setMessage(prefill.message);
      else if (prefill?.industry) setMessage(`Rubro: ${prefill.industry}`);
    }
  }, [isOpen, prefill]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && status !== 'submitting') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close, status]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  const isValid =
    name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    company.trim().length >= 2 &&
    acepto;

  const submit = async () => {
    if (!isValid || status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      await enviarSolicitudDeMiniPrixia({
        nombre: name.trim(),
        correo: email.trim(),
        empresa: company.trim(),
        telefono: phone.trim() || undefined,
        queQuiereResolver: message.trim() || undefined,
        aceptoContacto: true,
        origen: 'landing-modal',
        sitioWeb: trampa,
        msEnElFormulario: medir.current(),
      });
      setStatus('success');
    } catch (e) {
      setStatus('error');
      const fallo = e instanceof ErrorAlEnviarLaSolicitud ? e.fallo : null;
      setErrorMsg(
        fallo?.tipo === 'LIMITE'
          ? 'Recibimos varios pedidos desde tu red. Prueba en una hora.'
          : fallo?.tipo === 'DATOS'
            ? (Object.values(fallo.campos)[0] ?? 'Revisa los datos del formulario.')
            : 'No pudimos enviarlo. Revisa tu conexión: lo que escribiste sigue aquí, vuelve a intentarlo.'
      );
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
            onClick={() => status !== 'submitting' && close()}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 24, stiffness: 280 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="pointer-events-auto w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-800">
              {status === 'success' ? (
                <div className="p-10 text-center" role="status">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    Listo, {name.trim()}.
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-7">
                    Te escribimos a{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">{email}</span>{' '}
                    para coordinar tu Mini Prixia.
                  </p>
                  <button
                    onClick={close}
                    className="px-6 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative px-6 sm:px-8 pt-6 pb-4 border-b border-gray-200 dark:border-gray-800">
                    <button
                      onClick={() => status !== 'submitting' && close()}
                      disabled={status === 'submitting'}
                      className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex items-center justify-center disabled:opacity-50"
                      aria-label="Cerrar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 text-blue-700 dark:text-blue-400 text-xs font-semibold mb-2">
                      <Sparkles className="w-3.5 h-3.5" />
                      Mini Prixia
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      Pide tu Mini Prixia
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Te invitamos con un enlace personal y la recorres 15 días desde que entras,
                      guiada por misiones.
                    </p>
                  </div>

                  <div className="p-6 sm:p-8 space-y-5">
                    {/* Trampa para robots: invisible y fuera del orden de tabulación. */}
                    <div
                      aria-hidden="true"
                      className="absolute -left-[9999px] h-px w-px overflow-hidden"
                    >
                      <label htmlFor="demo-sitio-web">Sitio web</label>
                      <input
                        id="demo-sitio-web"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={trampa}
                        onChange={(e) => setTrampa(e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field
                        label="Nombre *"
                        value={name}
                        onChange={setName}
                        placeholder="Tu nombre"
                      />
                      <Field
                        label="Correo de trabajo *"
                        value={email}
                        onChange={setEmail}
                        placeholder="tu@empresa.com"
                        type="email"
                      />
                      <Field
                        label="Empresa *"
                        value={company}
                        onChange={setCompany}
                        placeholder="Nombre de tu empresa"
                      />
                      <Field
                        label="Teléfono / WhatsApp"
                        value={phone}
                        onChange={setPhone}
                        placeholder="+591 7..."
                        optional
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wider">
                        ¿Qué quieres resolver? (opcional)
                      </label>
                      <textarea
                        value={message}
                        maxLength={1000}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Por ejemplo: saber a cuánto vende mi competencia."
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>

                    <label className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={acepto}
                        onChange={(e) => setAcepto(e.target.checked)}
                      />
                      <span>Acepto que Prixia guarde estos datos y me contacte sobre Prixia.</span>
                    </label>

                    {status === 'error' && errorMsg && (
                      <div
                        role="alert"
                        className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400"
                      >
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{errorMsg}</span>
                      </div>
                    )}

                    <button
                      onClick={submit}
                      disabled={!isValid || status === 'submitting'}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
                        isValid && status !== 'submitting'
                          ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-lg shadow-blue-600/25 hover:shadow-[0_0_30px_rgba(99,102,241,0.3)] dark:hover:shadow-[0_0_30px_rgba(99,102,241,0.4)]'
                          : 'bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      }`}
                    >
                      {status === 'submitting' ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enviando…
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          {status === 'error' ? 'Reintentar' : 'Pedir mi Mini Prixia'}
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  optional,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  optional?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wider">
        {label}
        {optional && <span className="ml-1 text-gray-400 normal-case font-normal">(opcional)</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
}
